/**
 * DriveSmartLocator — iter182
 *
 * The one-click flow the operator asked for:
 *
 *   1. Click "Connect Drive"  — opens Google's consent screen, returns
 *      to this page with the ``drive.readonly`` scope granted.
 *   2. Click "Learn all file locations"  — backend walks every DB
 *      column that holds a file path, picks one sample row each,
 *      searches the connected Drive for that file, and registers
 *      the parent folder as ``(table, column) → folder_id``.
 *
 * After that the operator never types another folder ID. Every later
 * file lookup uses the registered folder — one Drive call per file.
 *
 * The component renders three states:
 *   - "Not connected"  — show the Connect CTA + an explanation.
 *   - "Connected, nothing learned yet"  — show Learn-all + status.
 *   - "Connected, registry populated"  — show the field-folder table
 *     + per-row Clear + bulk Learn / Clear buttons.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, Warning, Sparkle, Plug, Trash, ArrowsClockwise, Copy, Pulse } from "@phosphor-icons/react";
import {
  getLocatorStatus,
  learnAllDriveFields,
  clearLocatorRegistry,
  startDriveOAuth,
  getDriveOAuthStatus,
  testDriveOAuthConnection,
} from "@/lib/api";


export default function DriveSmartLocator({ user }) {
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [registry, setRegistry] = useState({});
  const [learning, setLearning] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [learnSummary, setLearnSummary] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  // iter183 — Drive-owner email hint + redirect URI surfacing.
  const [loginHint, setLoginHint] = useState(
    typeof window !== "undefined"
      ? (localStorage.getItem("drive_login_hint") || "")
      : ""
  );
  const [redirectUri, setRedirectUri] = useState("");
  const [oauthErrorReason, setOauthErrorReason] = useState("");
  // iter184 — "Test connection" result. Null = never tested this
  // session; otherwise {ok, email, display_name, storage_used_bytes,
  // storage_limit_bytes, error, error_detail}.
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const refresh = async () => {
    try {
      const r = await getLocatorStatus(user.email);
      setConnected(Boolean(r?.drive_connected));
      setRegistry(r?.registry || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not read locator status");
    }
  };

  const refreshOAuthMeta = async () => {
    // Fetch the exact redirect URI the backend will send to Google —
    // operator needs to paste this into Cloud Console → OAuth Client
    // → Authorized redirect URIs to clear the redirect_uri_mismatch
    // error.
    try {
      const r = await getDriveOAuthStatus(user.email);
      setRedirectUri(r?.redirect_uri_to_register || "");
    } catch (_e) { /* non-fatal */ }
  };

  useEffect(() => {
    refresh().finally(() => setLoading(false));
    refreshOAuthMeta();
    if (typeof window !== "undefined") {
      // Catch ?drive_oauth=success / error in the URL after the
      // Google callback redirects us back.
      const sp = new URLSearchParams(window.location.search);
      const ok = sp.get("drive_oauth");
      const reason = sp.get("reason") || "";
      if (ok === "success") {
        setTimeout(refresh, 500);
        setMsg("Google Drive connected. Click 'Learn all file locations' next.");
        // Strip the query string so refresh doesn't re-trigger.
        try { window.history.replaceState({}, "", window.location.pathname); } catch (_e) { /* noop */ }
      } else if (ok === "error") {
        setOauthErrorReason(reason || "unknown");
        setErr(`Google rejected the OAuth request: ${reason || "unknown error"}`);
      }
    }
  }, []);

  const connect = async () => {
    setConnecting(true); setErr(""); setMsg(""); setOauthErrorReason("");
    try {
      // Persist the hint locally so a reconnect later remembers it.
      if (typeof window !== "undefined") {
        if (loginHint) localStorage.setItem("drive_login_hint", loginHint);
        else localStorage.removeItem("drive_login_hint");
      }
      const r = await startDriveOAuth(
        user.email, window.location.pathname, loginHint.trim(),
      );
      if (r?.auth_url) {
        window.location.href = r.auth_url;
      } else {
        setErr("Did not receive a Google consent URL.");
      }
    } catch (e) {
      setConnecting(false);
      setErr(e?.response?.data?.detail || "Could not start OAuth flow");
    }
  };

  const copyRedirectUri = () => {
    if (!redirectUri) return;
    try {
      navigator.clipboard.writeText(redirectUri);
      setMsg("Redirect URI copied. Paste it into Google Cloud Console → OAuth Client → Authorized redirect URIs.");
    } catch (_e) { /* fallback: leave it visible */ }
  };

  const learnAll = async () => {
    setLearning(true); setErr(""); setMsg(""); setLearnSummary(null);
    try {
      const r = await learnAllDriveFields(user.email);
      setLearnSummary(r?.summary || {});
      setRegistry(r?.registry || {});
      const registeredCount = Object.values(r?.summary || {}).filter(
        (v) => v?.status === "registered").length;
      setMsg(`Learned ${registeredCount} field${registeredCount === 1 ? "" : "s"}.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not learn field locations");
    } finally { setLearning(false); }
  };

  const clearAll = async () => {
    setErr(""); setMsg("");
    try {
      const r = await clearLocatorRegistry(user.email);
      setRegistry(r?.registry || {});
      setMsg(`Cleared ${r?.deleted || 0} registration${r?.deleted === 1 ? "" : "s"}.`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not clear registry");
    }
  };

  const clearOne = async (table, column) => {
    setErr(""); setMsg("");
    try {
      const r = await clearLocatorRegistry(user.email, table, column);
      setRegistry(r?.registry || {});
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not clear that entry");
    }
  };

  // iter184 — Verify the stored OAuth token by pinging
  // ``drive.about.get`` and showing the connected account email +
  // storage. Lets the operator confirm the right Google account
  // ended up authorized BEFORE running the (slow) learn-all scan.
  const testConnection = async () => {
    setTesting(true); setErr(""); setMsg(""); setTestResult(null);
    try {
      const r = await testDriveOAuthConnection(user.email);
      setTestResult(r);
      if (!r?.ok) setErr(r?.error_detail || `Test failed: ${r?.error || "unknown"}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not test the Drive connection");
    } finally { setTesting(false); }
  };

  const fmtBytes = (n) => {
    if (!n && n !== 0) return "—";
    if (n < 1024) return `${n} B`;
    const units = ["KB", "MB", "GB", "TB"];
    let v = n / 1024; let i = 0;
    while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
    return `${v.toFixed(v < 10 ? 2 : 1)} ${units[i]}`;
  };

  if (loading) return null;
  const entries = Object.entries(registry);

  return (
    <div className="pt-4 border-t border-slate-200 space-y-3"
          data-testid="drive-smart-locator-section">
      <div className="flex items-center gap-2 flex-wrap">
        <Sparkle size={14} weight="bold" className="text-blue-700" />
        <span className="text-xs font-bold uppercase tracking-wider text-slate-700">Smart File Locator</span>
        <span className={"text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border "
                          + (connected
                                ? "text-emerald-700 bg-emerald-50 border-emerald-200"
                                : "text-amber-700 bg-amber-50 border-amber-200")}
              data-testid="drive-locator-status-badge">
          {connected ? "Drive connected" : "Drive not connected"}
        </span>
      </div>
      <p className="text-[11px] text-slate-500">
        One click connects your Google Drive (read-only) and one more
        click teaches the app where every file column lives. After
        that you&apos;ll never need to paste a folder ID — when a new DB
        field shows up with a file path, the locator searches Drive
        for that file and remembers the folder it found.
      </p>

      {!connected && (
        <div className="space-y-2">
          {/* iter183 — explicit "whose Drive?" hint so the consent
              screen pre-selects the right Google account (the one
              that owns the Drive folders, which may be different
              from whoever is signed into the app). */}
          <div>
            <label className="text-[11px] font-semibold text-slate-600 mb-0.5 block">
              Whose Drive holds the files? <span className="text-[10px] text-slate-400 font-normal">(optional — pre-selects this Google account on the consent screen)</span>
            </label>
            <Input
              data-testid="drive-locator-login-hint"
              type="email"
              value={loginHint}
              onChange={(e) => setLoginHint(e.target.value)}
              placeholder="e.g. info@usacompressor.com"
              className="h-9 text-sm rounded-sm border-slate-300"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button"
                    data-testid="drive-locator-connect"
                    onClick={connect}
                    disabled={connecting}
                    className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5">
              <Plug size={14} weight="bold" />
              {connecting ? "Opening Google…" : "Connect Drive"}
            </Button>
          </div>
        </div>
      )}

      {/* iter183 — when Google rejected the request (most commonly
          ``redirect_uri_mismatch``), surface the exact callback URL
          the operator needs to register in Cloud Console. */}
      {(oauthErrorReason === "redirect_uri_mismatch"
          || (!connected && redirectUri)) && (
        <div className="text-[11px] text-slate-700 bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-2 space-y-1"
              data-testid="drive-locator-redirect-uri">
          <div className="font-bold text-slate-800">
            {oauthErrorReason === "redirect_uri_mismatch"
              ? "Cloud Console fix needed"
              : "If you see \"Error 400: redirect_uri_mismatch\""}
          </div>
          <p>
            Add this exact URL to your OAuth client&apos;s <b>Authorized redirect URIs</b> in{" "}
            <a className="text-blue-700 underline" href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noopener noreferrer">Google Cloud Console → Credentials</a> → pick your OAuth 2.0 Client ID → <b>Edit</b>:
          </p>
          <div className="flex items-center gap-2 mt-1">
            <code data-testid="drive-locator-redirect-uri-value"
                   className="flex-1 font-mono text-[11px] bg-white border border-slate-300 rounded-sm px-2 py-1 truncate">
              {redirectUri || "(loading…)"}
            </code>
            {redirectUri && (
              <button
                data-testid="drive-locator-copy-redirect"
                onClick={copyRedirectUri}
                title="Copy"
                className="shrink-0 h-7 w-7 inline-flex items-center justify-center rounded-sm border border-slate-300 bg-white hover:bg-slate-100 text-slate-600">
                <Copy size={12} weight="bold" />
              </button>
            )}
          </div>
        </div>
      )}

      {connected && (
        <div className="flex flex-wrap gap-2">
          <Button type="button"
                  data-testid="drive-locator-test"
                  onClick={testConnection}
                  disabled={testing}
                  title="Ping Google to verify the token works and show which account is connected"
                  className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <Pulse size={13} weight="bold" />
            {testing ? "Pinging Google…" : "Test connection"}
          </Button>
          <Button type="button"
                  data-testid="drive-locator-learn"
                  onClick={learnAll}
                  disabled={learning}
                  className="rounded-sm bg-blue-700 text-white hover:bg-blue-800 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">
            <Sparkle size={14} weight="bold" />
            {learning ? "Searching Drive…" : entries.length > 0 ? "Re-learn all" : "Learn all file locations"}
          </Button>
          {entries.length > 0 && (
            <Button type="button"
                    data-testid="drive-locator-clear"
                    onClick={clearAll}
                    className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5">
              <Trash size={13} weight="bold" />
              Clear registry
            </Button>
          )}
          <Button type="button"
                  data-testid="drive-locator-reconnect"
                  onClick={connect}
                  disabled={connecting}
                  title="Sign in with a different Google account (e.g. the one that owns the Drive)"
                  className="rounded-sm border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 h-9 px-3 text-sm font-semibold inline-flex items-center gap-1.5">
            <ArrowsClockwise size={13} weight="bold" />
            Switch / Reconnect
          </Button>
        </div>
      )}

      {/* iter184 — Test-connection result panel. Shows the connected
          Google account email + storage so the operator can confirm
          the right Drive is authorized before running learn-all. */}
      {testResult?.ok && (
        <div className="text-[12px] bg-emerald-50 border border-emerald-200 rounded-sm px-3 py-2 space-y-1"
              data-testid="drive-locator-test-result">
          <div className="flex items-center gap-2">
            <CheckCircle size={14} weight="fill" className="text-emerald-700 shrink-0" />
            <span className="font-bold text-emerald-800">Drive is connected</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-3 gap-y-0.5 pl-6">
            <div>
              <span className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold block">Account</span>
              <span data-testid="drive-locator-test-email" className="font-mono text-slate-800 break-all">{testResult.email || "—"}</span>
            </div>
            {testResult.display_name && (
              <div>
                <span className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold block">Name</span>
                <span className="text-slate-800">{testResult.display_name}</span>
              </div>
            )}
            <div>
              <span className="text-slate-500 text-[10px] uppercase tracking-wider font-semibold block">Storage</span>
              <span className="font-mono text-slate-800">
                {fmtBytes(testResult.storage_used_bytes)}
                {testResult.storage_limit_bytes ? ` / ${fmtBytes(testResult.storage_limit_bytes)}` : ""}
              </span>
            </div>
          </div>
        </div>
      )}

      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1.5"
              data-testid="drive-locator-error">
          {err}
        </div>
      )}
      {msg && (
        <div className="text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1.5"
              data-testid="drive-locator-msg">
          {msg}
        </div>
      )}

      {entries.length > 0 && (
        <div className="border border-slate-200 rounded-sm overflow-hidden"
              data-testid="drive-locator-registry">
          <table className="w-full text-[12px]">
            <thead className="bg-slate-50 text-left text-slate-600">
              <tr>
                <th className="px-2.5 py-1.5 font-semibold">Table.Column</th>
                <th className="px-2.5 py-1.5 font-semibold">Drive folder</th>
                <th className="px-2.5 py-1.5 font-semibold">Sample path</th>
                <th className="px-2.5 py-1.5 font-semibold w-8"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map(([key, v]) => {
                const [tbl, col] = key.split(".");
                return (
                  <tr key={key}
                       data-testid={`drive-locator-row-${key}`}
                       className="border-t border-slate-100 align-top">
                    <td className="px-2.5 py-1.5 font-mono text-slate-700 whitespace-nowrap">
                      <CheckCircle size={11} weight="fill" className="text-emerald-600 inline mr-1" />
                      {key}
                    </td>
                    <td className="px-2.5 py-1.5">
                      <div className="font-semibold text-slate-800 truncate">{v.folder_name || "—"}</div>
                      <div className="font-mono text-[10px] text-slate-500 truncate">{v.folder_id}</div>
                    </td>
                    <td className="px-2.5 py-1.5 font-mono text-[11px] text-slate-600 break-all">{v.sample_path || "—"}</td>
                    <td className="px-2.5 py-1.5 text-right">
                      <button
                        data-testid={`drive-locator-row-clear-${key}`}
                        onClick={() => clearOne(tbl, col)}
                        title="Forget this mapping (next file lookup re-discovers it)"
                        className="text-slate-400 hover:text-red-700 p-1">
                        <Trash size={12} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {learnSummary && (
        <details className="text-[11px]" data-testid="drive-locator-learn-summary">
          <summary className="cursor-pointer font-semibold text-slate-600 hover:text-slate-900 select-none">
            Last learn run — details
          </summary>
          <div className="mt-1.5 border border-slate-200 rounded-sm overflow-hidden">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr>
                  <th className="px-2 py-1 font-semibold">Field</th>
                  <th className="px-2 py-1 font-semibold">Status</th>
                  <th className="px-2 py-1 font-semibold">Detail</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(learnSummary).map(([key, v]) => (
                  <tr key={key} className="border-t border-slate-100 align-top">
                    <td className="px-2 py-1 font-mono">{key}</td>
                    <td className="px-2 py-1">
                      {v.status === "registered" ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700">
                          <CheckCircle size={11} weight="fill" /> {v.status}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-amber-700">
                          <Warning size={11} weight="fill" /> {v.status}
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-600 break-all">
                      {v.folder_name && <span className="font-semibold">{v.folder_name}</span>}
                      {v.reason && <span className="italic">{v.reason}</span>}
                      {v.sample_path && (
                        <div className="font-mono text-[10px] text-slate-500 truncate">{v.sample_path}</div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </div>
  );
}
